import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import BZAPIService from '../../../services/BZAPIService';
import { IcoSearch, IcoX } from '../../bz/icons';
import {
  AREAS,
  DEGRADED_CALLOUT,
  OUTCOMES,
  OutcomePill,
  RunPill,
  areaByKey,
  areaShape,
  formatChecked,
  formatRunTime,
  runStatusKey,
  sortAreas,
  worstArea
} from '../../bz/runHealth';

// Source Health -> Run requirements (STRY0038849): what each vendor's bug run
// needs, checked at the start of every run. A vendor list on the left (status,
// one dot per requirement area, what needs attention) and the selected vendor's
// areas on the right. Reads GET /dashboard/runRequirements and shows only what
// it returns: area labels, outcomes, messages and times. A search box narrows the
// list by vendor name or id; it combines with the All / Needs attention filter.
// Platform -> Source Health and Prevent -> Run requirements both render this view.

const vendorShape = PropTypes.shape({
  vendorId: PropTypes.string.isRequired,
  name: PropTypes.string,
  run: PropTypes.shape({ status: PropTypes.string, endedAt: PropTypes.string }),
  areas: PropTypes.arrayOf(areaShape)
});

const NEEDS_ATTENTION = new Set(['stopped', 'retrying', 'degraded']);

// What the vendor's last run means, in one sentence. The areas say who acts.
const CALLOUTS = {
  stopped: 'Stopped at the health check. Nothing was saved, so no bugs are skipped.',
  retrying: 'The vendor was slow to answer. The run retries automatically; there is nothing to do.',
  degraded: DEGRADED_CALLOUT,
  operational: "Everything this vendor's run needs passed.",
  none: 'No requirement checks have run for this vendor yet.'
};
const CALLOUT_TONES = { stopped: 'bad', retrying: 'info', degraded: 'warn', operational: 'ok' };
const RUN_TONES = CALLOUT_TONES;

const nameOf = vendor => vendor.name || vendor.vendorId;
const statusOf = vendor => runStatusKey(vendor.run?.status);
const needsAttention = vendor => NEEDS_ATTENTION.has(statusOf(vendor));
const matchesQuery = (vendor, query) => {
  const q = query.trim().toLowerCase();
  return (
    !q ||
    nameOf(vendor)
      .toLowerCase()
      .includes(q) ||
    vendor.vendorId.toLowerCase().includes(q)
  );
};

// The vendor shown when nobody has picked one: the first that needs attention, else the first.
const defaultVendorId = vendors => (vendors.find(needsAttention) || vendors[0] || {}).vendorId || null;

// "4/5": requirement areas met, as a ring beside the vendor name. It counts
// areas, never probes, so the portal never shows how many checks a vendor runs.
// Met = the area passed. Green when all are met, else the run's colour.
const MET = new Set(['pass', 'empty']);
const RING_R = 16;
const RING_C = 2 * Math.PI * RING_R;

const RequirementsRing = ({ vendor }) => {
  const areas = sortAreas(vendor.areas);
  const status = statusOf(vendor);
  const checked = status !== 'none' && areas.length > 0;
  const met = checked ? areas.filter(a => MET.has(a.outcome)).length : 0;
  const tone = !checked ? 'none' : met === areas.length ? 'ok' : RUN_TONES[status] || 'none';
  const arc = checked ? (RING_C * met) / areas.length : 0;
  return (
    <span className={`rh-ring rh-ring--${tone}`}>
      <svg width="40" height="40" viewBox="0 0 40 40" aria-hidden="true">
        <circle className="rh-ring__track" cx="20" cy="20" r={RING_R} />
        {arc > 0 && (
          <circle
            className="rh-ring__arc"
            cx="20"
            cy="20"
            r={RING_R}
            strokeDasharray={`${arc} ${RING_C}`}
            transform="rotate(-90 20 20)"
          />
        )}
      </svg>
      <span className="rh-ring__text" aria-hidden="true">
        {checked ? `${met}/${areas.length}` : '–'}
      </span>
      <span className="rh-sr">
        {checked ? `${met} of ${areas.length} requirements met` : 'No requirement checks yet'}
      </span>
    </span>
  );
};
RequirementsRing.propTypes = { vendor: vendorShape.isRequired };

// What needs attention: the worst area and its message. Nothing when all passed.
const Attention = ({ vendor }) => {
  const worst = worstArea(vendor.areas);
  if (!worst) {
    const status = statusOf(vendor);
    return <span className="rh-item__why">{status === 'none' ? 'No checks yet' : 'All requirements met'}</span>;
  }
  return (
    <span className="rh-item__why">
      <b>{worst.label}:</b> {worst.message}
    </span>
  );
};
Attention.propTypes = { vendor: vendorShape.isRequired };

const VendorItem = ({ vendor, selected, onSelect, detailsId }) => (
  <li>
    <button
      type="button"
      className={`rh-item${selected ? ' is-selected' : ''}`}
      aria-current={selected ? 'true' : undefined}
      aria-controls={detailsId}
      onClick={() => onSelect(vendor.vendorId)}
    >
      <span className="rh-item__body">
        <span className="rh-item__top">
          <span className="rh-item__name" title={nameOf(vendor)}>
            {nameOf(vendor)}
          </span>
          <RunPill status={vendor.run?.status} short />
        </span>
        <Attention vendor={vendor} />
      </span>
      {/* After the name in reading order; drawn first (CSS order). */}
      <RequirementsRing vendor={vendor} />
    </button>
  </li>
);
VendorItem.propTypes = {
  vendor: vendorShape.isRequired,
  selected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  detailsId: PropTypes.string.isRequired
};

const FilterChip = ({ on, count, onClick, children }) => (
  <button type="button" className={`rh-chip${on ? ' is-on' : ''}`} aria-pressed={on} onClick={onClick}>
    <b>{count}</b> {children}
  </button>
);
FilterChip.propTypes = {
  on: PropTypes.bool.isRequired,
  count: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired
};

const AreaTile = ({ area, runIso }) => {
  const tone = OUTCOMES[area.outcome]?.tone;
  return (
    <div className={`rh-tile${tone === 'bad' || tone === 'warn' ? ` rh-tile--${tone}` : ''}`}>
      <div className="rh-tile__head">
        <b>{area.label}</b>
        <OutcomePill outcome={area.outcome} />
      </div>
      <p>{area.message}</p>
      <span className="bz-mono rh-tile__when">Checked {formatChecked(area.checkedAt, area.reused, runIso)}</span>
    </div>
  );
};
AreaTile.propTypes = { area: areaShape.isRequired, runIso: PropTypes.string };

const VendorDetails = ({ vendor, id }) => {
  const status = statusOf(vendor);
  const when = formatRunTime(vendor.run?.endedAt);
  const byKey = areaByKey(vendor.areas);
  const checked = status !== 'none' && byKey.size > 0;
  const tone = CALLOUT_TONES[status];
  // Only the areas the vendor checks get a tile; the rest are named in one line.
  const notUsed = AREAS.filter(({ key }) => !byKey.has(key)).map(({ label }) => label);
  return (
    // A div, not a section: the legacy theme pads every <section>.
    <div className="rh-pane" role="region" id={id} aria-label={`${nameOf(vendor)} requirements`}>
      <div className="rh-pane__head">
        <div>
          <h3 className="rh-pane__name">{nameOf(vendor)}</h3>
          <div className="bz-row" style={{ gap: 10, flexWrap: 'wrap' }}>
            <RunPill status={vendor.run?.status} />
            {when && <span className="bz-mono rh-pane__when">Last run {when}</span>}
          </div>
        </div>
        <Link className="bz-btn" to={`/vendors/${encodeURIComponent(vendor.vendorId)}/configuration`}>
          Open vendor
        </Link>
      </div>
      <div className="rh-pane__body">
        <div className={`rh-callout${tone ? ` rh-callout--${tone}` : ''}`}>{CALLOUTS[status]}</div>
        {checked && (
          <div className="rh-tiles">
            {[...byKey.values()].map(area => (
              <AreaTile key={area.area} area={area} runIso={vendor.run?.endedAt} />
            ))}
          </div>
        )}
        {checked && notUsed.length > 0 && (
          <div className="rh-notused">Not used by this vendor: {notUsed.join(', ')}</div>
        )}
      </div>
    </div>
  );
};
VendorDetails.propTypes = { vendor: vendorShape.isRequired, id: PropTypes.string.isRequired };

const RunRequirementsView = ({ reloadToken }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [onlyAttention, setOnlyAttention] = useState(false);
  const [query, setQuery] = useState('');
  // A Refresh pressed while a load is in flight wins; the older answer is dropped.
  const latest = useRef(0);

  const load = useCallback(async () => {
    const call = ++latest.current;
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const response = await BZAPIService.getRunRequirements();
      if (call !== latest.current) return;
      const loaded = (Array.isArray(response?.vendors) ? response.vendors : []).filter(v => v && v.vendorId);
      setVendors(loaded);
      setSelectedId(current => (loaded.some(v => v.vendorId === current) ? current : defaultVendorId(loaded)));
    } catch (error) {
      if (call !== latest.current) return;
      console.error('Failed to load run requirements:', error);
      setLoadFailed(true);
      setVendors([]);
    } finally {
      if (call === latest.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, reloadToken]);

  const selected = vendors.find(v => v.vendorId === selectedId) || null;
  const attentionCount = vendors.filter(needsAttention).length;
  const shown = (onlyAttention ? vendors.filter(needsAttention) : vendors).filter(v => matchesQuery(v, query));
  const detailsId = 'run-requirements-details';

  if (isLoading && vendors.length === 0) {
    return (
      <div className="bz-card text-center" style={{ padding: 40 }}>
        <Spinner color="primary" />
        <div className="bz-muted" style={{ marginTop: 8 }}>
          Loading run requirements…
        </div>
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className="bz-card text-center" style={{ padding: 40 }} role="status">
        <div style={{ fontWeight: 600 }}>Couldn&apos;t load run requirements</div>
        <div className="bz-muted" style={{ marginTop: 4 }}>
          Try Refresh in a moment.
        </div>
      </div>
    );
  }

  if (vendors.length === 0) {
    return (
      <div className="bz-card text-center" style={{ padding: 40 }}>
        <div style={{ fontWeight: 600 }}>No vendors to show yet</div>
        <div className="bz-muted" style={{ marginTop: 4 }}>
          Each vendor that is switched on and configured appears here after its first run.
        </div>
      </div>
    );
  }

  return (
    <div className="rh-split">
      <nav className="rh-list" aria-label="Vendors">
        <div className="rh-list__head">
          <FilterChip on={!onlyAttention} count={vendors.length} onClick={() => setOnlyAttention(false)}>
            All
          </FilterChip>
          <FilterChip on={onlyAttention} count={attentionCount} onClick={() => setOnlyAttention(true)}>
            Needs attention
          </FilterChip>
          <label className="bz-input rh-search">
            <IcoSearch size={14} />
            <input
              type="search"
              value={query}
              placeholder="Search vendors"
              aria-label="Search vendors"
              onChange={e => setQuery(e.target.value)}
            />
            {query && (
              <button type="button" className="rh-search__clear" aria-label="Clear search" onClick={() => setQuery('')}>
                <IcoX size={12} />
              </button>
            )}
          </label>
        </div>
        {shown.length > 0 ? (
          <ul className="rh-list__items">
            {shown.map(v => (
              <VendorItem
                key={v.vendorId}
                vendor={v}
                selected={v.vendorId === selectedId}
                onSelect={setSelectedId}
                detailsId={detailsId}
              />
            ))}
          </ul>
        ) : (
          <div className="bz-muted rh-list__empty">
            {query.trim() ? (
              <>No vendor matches &ldquo;{query.trim()}&rdquo;.</>
            ) : (
              <>Every vendor&apos;s last run passed.</>
            )}
          </div>
        )}
      </nav>
      {selected && <VendorDetails vendor={selected} id={detailsId} />}
    </div>
  );
};

RunRequirementsView.propTypes = {
  // Bumped by the page's Refresh button.
  reloadToken: PropTypes.number
};

export default RunRequirementsView;
